import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import type { ApiError } from '@/core/api';
import type { IcpSegment, IcpSegmentInput } from '../types';

const schema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(100),
  description: z.string(),
});

type FormValues = z.infer<typeof schema>;

interface IcpSegmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Edit this segment; create when omitted. */
  segment?: IcpSegment;
  onSubmit: (input: IcpSegmentInput) => Promise<unknown>;
}

export function IcpSegmentDialog({ open, onOpenChange, segment, onSubmit }: IcpSegmentDialogProps) {
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', description: '' },
  });

  useEffect(() => {
    if (open) {
      form.reset({ name: segment?.name ?? '', description: segment?.description ?? '' });
    }
  }, [open, segment, form]);

  async function handleSubmit(values: FormValues) {
    try {
      await onSubmit(values);
      onOpenChange(false);
    } catch (e) {
      const err = e as ApiError;
      for (const [field, messages] of Object.entries(err?.fieldErrors ?? {})) {
        form.setError(field === 'description' ? 'description' : 'name', {
          message: messages.join(' '),
        });
      }
    }
  }

  const submitting = form.formState.isSubmitting;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{segment ? 'Edit ICP segment' : 'New ICP segment'}</DialogTitle>
          <DialogDescription>
            One of your ideal customer profiles — leads are tagged with the one they match.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="EU SaaS scale-ups" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Profile</FormLabel>
                  <FormControl>
                    <Textarea
                      rows={4}
                      placeholder="Who fits: industry, size, region, stack, budget…"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Saving…' : segment ? 'Save' : 'Create segment'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
